import React from 'react';
import { PurchaseHistory } from 'ai-api/types/purchase';
import './PurchaseHistoryItem.scss';
import { formatDate } from '../../Utils/formatting';

interface PurchaseHistoryItemProps {
    purchase: PurchaseHistory;
    onViewDetails: (purchase: PurchaseHistory) => void;
}

const PurchaseHistoryItem: React.FC<PurchaseHistoryItemProps> = ({ purchase, onViewDetails }) => {
    const getPurchaseStatusChip = (status: string) => {
        const statusClasses: Record<string, string> = {
            'created': 'status-created',
            'waiting': 'status-waiting',
            'succeeded': 'status-succeeded',
            'failed': 'status-failed',
            'canceled': 'status-canceled'
        };

        const statusLabels: Record<string, string> = {
            'created': 'Created',
            'waiting': 'Processing',
            'succeeded': 'Succeeded',
            'failed': 'Failed',
            'canceled': 'Canceled'
        };

        return (
            <span className={`status-chip ${statusClasses[status] || 'status-unknown'}`}>
                {statusLabels[status] || status}
            </span>
        );
    };


    // Only subscription purchases could have upcoming sessions
    const isUpcoming = purchase.status === 'succeeded' && purchase.type === 'subscription';

    return (
        <div className="purchase-history-item">
            <div className="purchase-header">
                <div className="purchase-image">
                    <img
                        src={'/feature1.png'}
                        alt={purchase.productName}
                    />
                </div>
                <div className="purchase-title">
                    <h3>{purchase.productName}</h3>
                    <div className="purchase-meta">
                        <span className="purchase-id">Order #{purchase.orderid && purchase.orderid.includes('_') ? purchase.orderid.split('_')[1] : purchase.orderid}</span>
                        <span className="purchase-date">Purchased on {formatDate(new Date(purchase.created_at * 1000).toISOString())}</span>
                        {purchase.type && (
                            <span className={`product-type-tag ${purchase.type}`}>
                                {purchase.type.charAt(0).toUpperCase() + purchase.type.slice(1)}
                            </span>
                        )}
                    </div>
                </div>
                <div className="purchase-status">
                    {getPurchaseStatusChip(purchase.status)}
                </div>
            </div>

            <div className="purchase-details">
                <div className="purchase-info">
                    {purchase.type === 'subscription' && (
                        <div className="info-item">
                            <span className="label">Type:</span>
                            <span className="value">Subscription</span>
                        </div>
                    )}

                    {purchase.type === 'coin' && (
                        <div className="info-item">
                            <span className="label">Type:</span>
                            <span className="value">Coin</span>
                        </div>
                    )}

                    {purchase.type === 'subscription' && purchase.remainder !== undefined && (
                        <div className="info-item">
                            <span className="label">Remaining Time:</span>
                            <span className="value">{Math.floor(purchase.remainder / (60 * 60 * 24))} days</span>
                        </div>
                    )}

                    {purchase.type === 'coin' && purchase.value !== undefined && (
                        <div className="info-item">
                            <span className="label">Value:</span>
                            <span className="value">{purchase.value} coins</span>
                        </div>
                    )}
                </div>

                <div className="purchase-actions">
                    <button
                        className="btn btn-details"
                        onClick={() => onViewDetails(purchase)}
                    >
                        View Details
                    </button>
                    
                    {isUpcoming && (
                        <button className="btn btn-primary">
                            View Session Details
                        </button>
                    )}

                    {purchase.status === 'succeeded' && isUpcoming && (
                        <button className="btn btn-secondary">
                            Reschedule
                        </button>
                    )}

                    {purchase.status === 'succeeded' && isUpcoming && (
                        <button className="btn btn-danger">
                            Cancel
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PurchaseHistoryItem;